import {useEffect, useMemo, useState} from "react";
import {Button, Input, InputGroup, Pagination, RadioTile, RadioTileGroup, Table, toaster, Tooltip, Whisper} from "rsuite";
import {DeviceType} from "../../../api/artifacts";
import {ArtifactsApi} from "../../../services/Artifacts.api";
import {
  AiOutlineCloseIcon,
  AiOutlineSearchIcon,
  FaPlusIcon,
} from "../../../models/Icons.model";
import {DEVICE_TYPE_OPTIONS, DEVICE_TYPE_SHORT_LABELS, FIRMWARE_TABLE_HEIGHT, FirmwareItem, FirmwaresResponse, PAGE_SIZE} from "../Firmwares.model";
import {FRONT_FILTER_PAGE_SIZE, FirmwaresUtils} from "../Firmwares.util";
import FirmwareModal from "./FirmwareModal";
import FirmwareDescriptionCell from "./FirmwareDescriptionCell";
import NotificationFactory from "../../../shared/Notifications";

const {Column, HeaderCell, Cell} = Table;

const DeviceFirmwares = () => {
  const [loading, setLoading] = useState(false);
  const [firmwares, setFirmwares] = useState<FirmwareItem[]>([]);
  const [page, setPage] = useState(1);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedFirmware, setSelectedFirmware] = useState<FirmwareItem | null>(null);
  const [searchValue, setSearchValue] = useState("");
  const [deviceType, setDeviceType] = useState<DeviceType | null>(null);

  const loadFirmwares = async () => {
    setLoading(true);

    try {
      const response = await ArtifactsApi.deviceFirmwares.getList(1, FRONT_FILTER_PAGE_SIZE) as FirmwaresResponse;
      setFirmwares(FirmwaresUtils.getItems<FirmwareItem>(response));
    } catch (error) {
      console.error("Ошибка получения прошивок:", error);
      toaster.push(NotificationFactory.error(FirmwaresUtils.getListErrorMessage(error)), {placement: "topEnd"});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFirmwares().then();
  }, []);

  const handleClearSearch = () => {
    setSearchValue("");
    setPage(1);
  };

  const handleDeviceTypeChange = (value: DeviceType | null) => {
    setDeviceType(value);
    setPage(1);
  };

  const filteredFirmwares = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();

    return firmwares.filter((firmware) => {
      const matchesDeviceType = !deviceType || firmware.deviceType === deviceType;
      const matchesSearch = !normalizedSearch || FirmwaresUtils.getSearchString(firmware).toLowerCase().includes(normalizedSearch);

      return matchesDeviceType && matchesSearch;
    });
  }, [firmwares, searchValue, deviceType]);

  const paginatedFirmwares = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;

    return filteredFirmwares.slice(start, start + PAGE_SIZE);
  }, [filteredFirmwares, page]);

  const handleOpenCreateModal = () => {
    setSelectedFirmware(null);
    setModalMode("create");
    setModalOpen(true);
  };

  const handleOpenEditModal = (firmware: FirmwareItem) => {
    setSelectedFirmware(firmware);
    setModalMode("edit");
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedFirmware(null);
  };

  const handleSaveSuccess = () => {
    handleCloseModal();
    loadFirmwares().then();
  };

  return (
    <>
      <div className="row mt-3 align-items-center firmwares-toolbar">
        <div className="col-md-1 col-sm-2 mb-3">
          <Whisper placement="top" speaker={<Tooltip>Добавить новую прошивку</Tooltip>}>
            <Button
              style={{height: 35}}
              size="md"
              appearance="primary"
              color="green"
              onClick={handleOpenCreateModal}
            >
              <FaPlusIcon />
            </Button>
          </Whisper>
        </div>

        <div className="col-md-4 col-sm-10 mb-3">
          <InputGroup>
            <InputGroup.Addon>
              <AiOutlineSearchIcon />
            </InputGroup.Addon>
            <Input
              placeholder="Поиск прошивок..."
              value={searchValue}
              onChange={(value) => {
                setSearchValue(value);
                setPage(1);
              }}
            />
            {searchValue && (
              <InputGroup.Button onClick={handleClearSearch}>
                <AiOutlineCloseIcon />
              </InputGroup.Button>
            )}
          </InputGroup>
        </div>
      </div>

      <div className="mb-3">
        <RadioTileGroup
          as="div"
          aria-label="Фильтр по типу устройства"
          className="w-100 firmwares-device-filter-group"
          value={deviceType || "all"}
          onChange={(value) => handleDeviceTypeChange(value === "all" ? null : value as DeviceType)}
        >
          <RadioTile value="all" label="Все">
            <span className="radio-tile-text">Все</span>
          </RadioTile>

          {DEVICE_TYPE_OPTIONS.map((option) => (
            <RadioTile
              key={option.value}
              value={option.value}
              label={DEVICE_TYPE_SHORT_LABELS[option.value]}
            >
              <span className="radio-tile-text">{DEVICE_TYPE_SHORT_LABELS[option.value]}</span>
            </RadioTile>
          ))}
        </RadioTileGroup>
      </div>

      <Table
        height={FIRMWARE_TABLE_HEIGHT}
        data={paginatedFirmwares}
        loading={loading}
        onRowClick={(rowData: FirmwareItem) => handleOpenEditModal(rowData)}
        className="users-table firmwares-table"
        rowHeight={60}
        wordWrap
      >
        <Column width={100} sortable verticalAlign="middle" fullText>
          <HeaderCell>Версия</HeaderCell>
          <Cell dataKey="version">
            {(rowData: FirmwareItem) => rowData.version || "-"}
          </Cell>
        </Column>

        <Column width={160} sortable verticalAlign="middle" fullText>
          <HeaderCell>Тип устройства</HeaderCell>
          <Cell dataKey="deviceType">
            {(rowData: FirmwareItem) => rowData.deviceType ? DEVICE_TYPE_SHORT_LABELS[rowData.deviceType] : "-"}
          </Cell>
        </Column>

        <Column width={350} sortable verticalAlign="middle">
          <HeaderCell>Описание</HeaderCell>
          <Cell dataKey="description">
            {(rowData: FirmwareItem) => <FirmwareDescriptionCell description={rowData.description} />}
          </Cell>
        </Column>

        <Column width={100} verticalAlign="middle" fullText>
          <HeaderCell>Файл</HeaderCell>
          <Cell>
            {(rowData: FirmwareItem) => rowData.fileDescriptor?.name || "-"}
          </Cell>
        </Column>

        <Column width={120} verticalAlign="middle" align="center">
          <HeaderCell className="users-table__cell-center">Размер</HeaderCell>
          <Cell>
            {(rowData: FirmwareItem) => FirmwaresUtils.formatFileSize(rowData.fileDescriptor?.size)}
          </Cell>
        </Column>

        <Column width={70} sortable verticalAlign="middle" align="center">
          <HeaderCell className="users-table__cell-center">Test</HeaderCell>
          <Cell dataKey="isDeployedToTest">
            {(rowData: FirmwareItem) => <FirmwaresUtils.DeployStateIcon enabled={rowData.isDeployedToTest} />}
          </Cell>
        </Column>

        <Column width={70} sortable verticalAlign="middle" align="center">
          <HeaderCell className="users-table__cell-center">One</HeaderCell>
          <Cell dataKey="isDeployedToProduction">
            {(rowData: FirmwareItem) => <FirmwaresUtils.DeployStateIcon enabled={rowData.isDeployedToProduction} />}
          </Cell>
        </Column>

        <Column width={150} sortable verticalAlign="middle" fullText>
          <HeaderCell>Создал</HeaderCell>
          <Cell dataKey="createdBy">
            {(rowData: FirmwareItem) => rowData.createdBy || "-"}
          </Cell>
        </Column>

        <Column width={150} sortable verticalAlign="middle" align="center">
          <HeaderCell className="users-table__cell-center">Создано</HeaderCell>
          <Cell dataKey="createdAt">
            {(rowData: FirmwareItem) => FirmwaresUtils.formatDate(rowData.createdAt)}
          </Cell>
        </Column>
      </Table>

      <div className="d-flex justify-content-end mt-3">
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          layout={["total", "-", "pager"]}
          total={filteredFirmwares.length}
          limit={PAGE_SIZE}
          activePage={page}
          onChangePage={setPage}
        />
      </div>

      <FirmwareModal
        open={modalOpen}
        mode={modalMode}
        firmware={selectedFirmware}
        onClose={handleCloseModal}
        onSuccess={handleSaveSuccess}
      />
    </>
  );
};

export default DeviceFirmwares;
